/**
 * @fileoverview In-job "Apply workflow" entry point (MOR-696, renamed MOR-716)
 *
 * A dropdown on the per-job task surface that lists the firm's workflows
 * and applies the chosen one to the current job, minting its tasks +
 * dependencies. Due dates anchor to the job's appointment date automatically.
 * Owner/admin only — hidden otherwise (the apply mutation re-checks access).
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { LayoutTemplate, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { extractErrorMessage } from "@/utils/errors";

export function ApplyWorkflowButton({
  orgId,
  jobId,
}: {
  orgId: Id<"organizations">;
  jobId: Id<"jobs">;
}) {
  const { data: templates } = useQuery(
    convexQuery(api.tasks.workflows.listByOrg, { orgId }),
  );
  const applyTemplate = useConvexMutation(
    api.tasks.workflows.applyWorkflowToJob,
  );
  const [busyId, setBusyId] = useState<Id<"taskTemplates"> | null>(null);

  const rows = templates ?? [];

  const handleApply = async (templateId: Id<"taskTemplates">, name: string) => {
    setBusyId(templateId);
    try {
      const { created } = await applyTemplate({ templateId, jobId });
      toast.success(
        created > 0
          ? `Added ${created} task${created === 1 ? "" : "s"} from “${name}”.`
          : `“${name}” has no tasks yet.`,
      );
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not apply the workflow."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busyId !== null}>
          {busyId !== null ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : (
            <LayoutTemplate className="mr-1.5 size-3.5" />
          )}
          Apply workflow
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Workflows</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {rows.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            No workflows yet.{" "}
            <Link
              to="/dashboard/workflows"
              className="font-medium text-primary hover:underline"
            >
              Create one
            </Link>{" "}
            under Tasks &amp; Calendar → Workflows.
          </div>
        ) : (
          rows.map((t) => (
            <DropdownMenuItem
              key={t._id}
              onSelect={(e) => {
                e.preventDefault();
                void handleApply(t._id, t.name);
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate">{t.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {t.itemCount}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
